import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Editor } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

// Basic SQL keywords for formatting & completion fallback
const SQL_KEYWORDS = [
  'SELECT','FROM','WHERE','INNER JOIN','LEFT JOIN','RIGHT JOIN','FULL JOIN','JOIN','GROUP BY','ORDER BY','LIMIT','OFFSET','ON','AS','AND','OR','NOT','IN','IS','NULL','COUNT','SUM','AVG','MIN','MAX','DISTINCT','HAVING','CASE','WHEN','THEN','ELSE','END'
];

export interface SchemaMeta {
  tables: Record<string, { columns: string[]; types: Record<string,string> }>; // table -> column list & types
}

interface SQLEditorProps {
  initialValue: string;
  dialect: string;
  schemaMeta?: SchemaMeta | null;
  onValidate?: (isValid: boolean, errors: string[]) => void;
  onValueChange?: (value: string) => void;
  readOnly?: boolean;
}

const SQLEditor: React.FC<SQLEditorProps> = ({
  initialValue,
  dialect: _dialect, // Prefix with underscore to indicate intentionally unused
  schemaMeta,
  onValidate,
  onValueChange,
  readOnly = false
}) => {
  const [editorValue, setEditorValue] = useState(initialValue);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const monacoEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const completionDisposableRef = useRef<{ dispose: () => void } | null>(null);
  const actionDisposableRef = useRef<{ dispose: () => void } | null>(null);

  // Update editor value when initialValue changes
  useEffect(() => {
    setEditorValue(initialValue);
  }, [initialValue]);

  const handleEditorChange = useCallback((value: string | undefined) => {
    const newValue = value || '';
    setEditorValue(newValue);
    
    if (onValueChange) {
      onValueChange(newValue);
    }

    // Simple validation
    if (onValidate) {
      const hasContent = newValue.trim().length > 0;
      const hasSelectKeyword = newValue.toUpperCase().includes('SELECT');
      onValidate(hasContent && hasSelectKeyword, hasContent ? [] : ['SQL query cannot be empty']);
    }
  }, [onValueChange, onValidate]);

  // Build completion items based on schema
  const buildSchemaCompletions = useCallback(() => {
    if (!schemaMeta) return [];
    const items: any[] = [];
    Object.entries(schemaMeta.tables).forEach(([table, meta]) => {
      items.push({
        label: table,
        kind:  'Class',
        insertText: table,
        detail: 'Table',
        documentation: `${table} (columns: ${meta.columns.join(', ')})`,
      });
      meta.columns.forEach(col => {
        items.push({
          label: col,
          kind: 'Field',
          insertText: col,
          detail: `Column (${table})`,
          documentation: `${col} :: ${meta.types[col] || 'unknown'} in table ${table}`,
          sortText: '1_'+col
        });
        items.push({
          label: `${table}.${col}`,
            kind: 'Field',
            insertText: `${table}.${col}`,
            detail: `Qualified column`,
            documentation: `${table}.${col} :: ${meta.types[col] || 'unknown'}`,
            sortText: '0_'+table+'.'+col
        });
      });
    });
    // Simple SELECT snippet
    items.push({
      label: 'select_from',
      kind: 'Snippet',
      insertText: 'SELECT ${1:*}\nFROM ${2:table}\nWHERE ${3:condition};',
      insertTextRules: 'InsertAsSnippet',
      detail: 'SELECT snippet',
      documentation: 'Basic SELECT snippet with placeholders'
    });
    return items;
  }, [schemaMeta]);

  const registerCompletions = useCallback((monaco: typeof import('monaco-editor')) => {
    // Dispose previous provider
    if (completionDisposableRef.current) {
      completionDisposableRef.current.dispose();
    }
    const provider = monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: [' ', '.', ',','('],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        // Keyword items
        const keywordItems = SQL_KEYWORDS.map(k => ({
          label: k,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: k,
          range,
          sortText: '2_'+k
        }));
        const schemaItems = buildSchemaCompletions().map(item => ({
          ...item,
          kind: (monaco.languages.CompletionItemKind as any)[item.kind] || monaco.languages.CompletionItemKind.Variable,
          insertTextRules: item.insertTextRules === 'InsertAsSnippet' ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
          range
        }));
        return { suggestions: [...schemaItems, ...keywordItems] };
      }
    });
    completionDisposableRef.current = provider;
  }, [buildSchemaCompletions]);

  const formatSQL = useCallback((raw: string): string => {
    if (!raw.trim()) return raw;
    // Basic formatting: uppercase keywords, line breaks for major clauses
    let formatted = raw.replace(/\b(SELECT|FROM|WHERE|INNER JOIN|LEFT JOIN|RIGHT JOIN|FULL JOIN|JOIN|GROUP BY|ORDER BY|LIMIT|OFFSET|ON|AND|OR|NOT|IN|IS|NULL|HAVING)\b/gi, (m) => m.toUpperCase());
    // Split lines for major clauses
    formatted = formatted
      .replace(/\s+SELECT\s+/i, '\nSELECT ')
      .replace(/\s+FROM\s+/i, '\nFROM ')
      .replace(/\s+WHERE\s+/i, '\nWHERE ')
      .replace(/\s+GROUP BY\s+/i, '\nGROUP BY ')
      .replace(/\s+ORDER BY\s+/i, '\nORDER BY ')
      .replace(/\s+LIMIT\s+/i, '\nLIMIT ')
      .replace(/\s+INNER JOIN\s+/ig, '\nINNER JOIN ')
      .replace(/\s+LEFT JOIN\s+/ig, '\nLEFT JOIN ')
      .replace(/\s+RIGHT JOIN\s+/ig, '\nRIGHT JOIN ')
      .replace(/\s+JOIN\s+/ig, '\nJOIN ');
    // Indent continuation lines (naive)
    formatted = formatted.split('\n').map(line => {
      if (/^(FROM|WHERE|GROUP BY|ORDER BY|LIMIT|INNER JOIN|LEFT JOIN|RIGHT JOIN|JOIN)/.test(line.trim())) return line.trim();
      if (/^SELECT /.test(line.trim())) return line.trim();
      return '  ' + line.trim();
    }).join('\n');
    return formatted;
  }, []);

  const registerFormatAction = useCallback((monaco: typeof import('monaco-editor'), _editor: editor.IStandaloneCodeEditor) => {
    if (actionDisposableRef.current) actionDisposableRef.current.dispose();
    const disposable = _editor.addAction({
      id: 'format-sql-pretty',
      label: 'Format SQL (Pretty)',
      keybindings: [
        // Ctrl+Alt+L (Win/Linux & Mac approximation)
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Alt | monaco.KeyCode.KeyL,
      ],
      precondition: undefined,
      keybindingContext: undefined,
      contextMenuGroupId: 'navigation',
      contextMenuOrder: 1.5,
      run: () => {
        const current = _editor.getValue();
        const formatted = formatSQL(current);
        _editor.executeEdits('format', [{
          range: _editor.getModel()!.getFullModelRange(),
          text: formatted,
          forceMoveMarkers: true
        }]);
      }
    });
    actionDisposableRef.current = disposable;
  }, [formatSQL]);

  const handleEditorDidMount = useCallback((_editor: editor.IStandaloneCodeEditor, monaco: typeof import('monaco-editor')) => {
    monacoEditorRef.current = _editor;
    // Define custom theme aligned with tokens
    try {
      monaco.editor.defineTheme('odpm-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: '5c6370', fontStyle: 'italic' },
            { token: 'keyword', foreground: 'c678dd' },
            { token: 'number', foreground: 'd19a66' },
            { token: 'string', foreground: '98c379' },
            { token: 'operator', foreground: '56b6c2' },
            { token: 'identifier', foreground: 'abb2bf' },
        ],
        colors: {
          'editor.background': '#1f232a',
          'editor.foreground': '#abb2bf',
          'editorLineNumber.foreground': '#495162',
          'editorLineNumber.activeForeground': '#e6e6e6',
          'editorCursor.foreground': '#61afef',
          'editor.selectionBackground': '#3e445166',
          'editor.inactiveSelectionBackground': '#3e445144',
          'editor.lineHighlightBackground': '#2a2f36',
          'editorIndentGuide.background': '#3e4451',
          'editorIndentGuide.activeBackground': '#61afef66'
        }
      });
      monaco.editor.setTheme('odpm-dark');
    } catch {/* ignore theme errors */}
    setIsEditorReady(true);
    registerCompletions(monaco);
    registerFormatAction(monaco, _editor);
    console.log('Monaco Editor mounted successfully with schema-aware IntelliSense & custom theme');
  }, [registerCompletions, registerFormatAction]);

  // Re-register completions when schema changes
  useEffect(() => {
    if (!isEditorReady || !monacoEditorRef.current) return;
    // dynamic import monaco to access languages
    import('monaco-editor').then(monaco => {
      registerCompletions(monaco);
    }).catch(() => {/* ignore */});
  }, [schemaMeta, isEditorReady, registerCompletions]);

  const editorOptions: editor.IStandaloneEditorConstructionOptions = {
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 14,
    lineNumbers: 'on',
    readOnly,
    automaticLayout: true,
    wordWrap: 'on',
  theme: 'odpm-dark',
    tabSize: 2,
    insertSpaces: true,
    detectIndentation: true,
    formatOnType: true,
    formatOnPaste: true,
    smoothScrolling: true,
  };

  return (
    <div
      className="sql-editor-container"
      style={{
        height: '300px',
        border: '1px solid var(--odpm-border)',
        borderRadius: '6px',
        background: 'var(--odpm-panel)'
      }}
    >
      <Editor
        height="300px"
        language="sql"
        value={editorValue}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        options={editorOptions}
        loading={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            Loading SQL Editor...
          </div>
        }
      />
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 8px',fontSize:12,color:'var(--odpm-text-muted)',background:'var(--odpm-panel)',borderTop:'1px solid var(--odpm-border)'}}>
        <span>Ctrl+Alt+L to format • Type table or column names for suggestions</span>
        <button
          type="button"
          onClick={() => {
            if (monacoEditorRef.current) {
              const formatted = formatSQL(monacoEditorRef.current.getValue());
              monacoEditorRef.current.setValue(formatted);
            }
          }}
          style={{
            background:'var(--odpm-primary)',
            color:'var(--odpm-text-strong)',
            border:'1px solid var(--odpm-border)',
            padding:'2px 10px',
            borderRadius:4,
            cursor:'pointer'
          }}
        >Format</button>
      </div>
      
      {!isEditorReady && (
        <div style={{
          padding: '8px',
          background: 'linear-gradient(90deg, rgba(97,175,239,0.08), rgba(198,120,221,0.08))',
          fontSize: '12px',
          color: 'var(--odpm-text-muted)',
          borderTop: '1px solid var(--odpm-border)'
        }}>
          Loading Monaco Editor...
        </div>
      )}
    </div>
  );
};

export default SQLEditor;